// Jobber master-job links for the mechanical contracts.
//
// Each mechanical building is supposed to point at one Jobber job titled
// "{address} Mechanical Maintenance Master Job". That pointer is a single
// field on the Building record, jobberMechJobId, and everything downstream
// hangs off it: no field, no Jobber button on the mech schedule rows and no
// upcoming visits for that building.
//
// Until now the field was only ever written by POSTing to the link endpoints
// by hand, so it quietly fell behind whenever a contract was signed and a new
// master job was created in Jobber. This page is that curl, with the state
// visible: what is linked, what is not, and what Jobber is holding that no
// building has claimed.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaExclamationTriangle, FaExternalLinkAlt, FaLink, FaMagic,
  FaSearch, FaSyncAlt, FaUnlink,
} from 'react-icons/fa';
import API_BASE_URL from './config/api';
import './MaintenanceJobberLinks.css';

const authHeaders = () => {
  const token = localStorage.getItem('managerToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Jobber's own wording for where a job sits in its lifecycle. Anything that
// isn't active is worth a second look before trusting its visit dates.
const DORMANT_STATUSES = new Set(['archived', 'complete', 'completed', 'closed']);

const jobLabel = job => {
  const number = job.jobNumber ? `#${job.jobNumber}` : '';
  return [job.title, number].filter(Boolean).join('  ');
};

export default function MaintenanceJobberLinks() {
  const [data, setData] = useState({ buildings: [], masterJobs: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  // buildingId -> jobId the user has picked but not saved yet ('' = unlink)
  const [pending, setPending] = useState({});
  const [savingId, setSavingId] = useState('');
  const [notice, setNotice] = useState('');

  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');

  const load = useCallback(async signal => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/mechanical/jobber-link/status`, {
        headers: authHeaders(), signal,
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || body.error) throw new Error(body.error || `Status ${r.status}`);
      setData({
        buildings: Array.isArray(body.buildings) ? body.buildings : [],
        masterJobs: Array.isArray(body.masterJobs) ? body.masterJobs : [],
      });
      setError('');
      setPending({});
    } catch (e) {
      if (e.name === 'AbortError') return;
      setError(e.message || 'Could not reach Jobber.');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const { buildings, masterJobs } = data;

  // Every building pointing at a given job, not just one. A master job can
  // legitimately cover several addresses — "1, 9, 19 Four Winds Master Job"
  // is one job for three buildings — and link/set only writes the building
  // it is given, so sharing is normal rather than a conflict to resolve.
  const claimants = useMemo(() => {
    const map = {};
    buildings.forEach(b => {
      if (b.jobberMechJobId) (map[b.jobberMechJobId] ||= []).push(b.name);
    });
    return map;
  }, [buildings]);

  const knownJobIds = useMemo(
    () => new Set(masterJobs.map(j => j.id)),
    [masterJobs],
  );

  const jobsById = useMemo(() => {
    const map = {};
    masterJobs.forEach(j => { map[j.id] = j; });
    return map;
  }, [masterJobs]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return buildings.filter(b => !q
      || (b.name || '').toLowerCase().includes(q)
      || (b.address || '').toLowerCase().includes(q)
      || (b.jobberClientName || '').toLowerCase().includes(q)
      || (b.jobberMechJobTitle || '').toLowerCase().includes(q));
  }, [buildings, search]);

  const linkedCount = buildings.filter(b => b.jobberMechJobId).length;
  const unclaimed = masterJobs.filter(j => !claimants[j.id]);
  // A building pointing at an id Jobber no longer returns for the master-job
  // search: the job was renamed, archived out of reach, or deleted. The link
  // still opens something, which is exactly why it goes unnoticed.
  const stale = buildings.filter(b => b.jobberMechJobId && !knownJobIds.has(b.jobberMechJobId));

  const saveLink = async (building, jobId) => {
    setSavingId(building.id);
    setNotice('');
    try {
      const r = await fetch(`${API_BASE_URL}/api/mechanical/jobber-link/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ buildingId: building.id, jobId: jobId || null }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || body.error) throw new Error(body.error || `Status ${r.status}`);
      setNotice(jobId
        ? `${building.name} linked to ${jobsById[jobId] ? jobLabel(jobsById[jobId]) : 'the selected job'}.`
        : `${building.name} unlinked.`);
      await load();
    } catch (e) {
      setError(e.message || 'Could not save that link.');
    } finally {
      setSavingId('');
    }
  };

  const runAuto = async dryRun => {
    setBusy(dryRun ? 'preview' : 'apply');
    setNotice('');
    try {
      const r = await fetch(`${API_BASE_URL}/api/mechanical/jobber-link/auto`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ dryRun }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || body.error) throw new Error(body.error || `Status ${r.status}`);
      const changed = (body.linked || []).filter(l => l.changed);
      if (dryRun) {
        setPreview({ changed, unmatched: body.unmatched || [] });
      } else {
        setPreview(null);
        setNotice(changed.length
          ? `Linked ${changed.length} building${changed.length === 1 ? '' : 's'}.`
          : 'Everything was already up to date.');
        await load();
      }
    } catch (e) {
      setError(e.message || 'Auto-match failed.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="jobber-links">
      <div className="jobber-links__header">
        <div>
          <h1>Jobber Links</h1>
          <p>
            Which Jobber master job each mechanical building points at. A building with
            no link shows no Jobber button on the schedule and reports no upcoming visits.
          </p>
        </div>
        <div className="jobber-links__actions">
          <button
            type="button"
            className="jobber-links__button"
            onClick={() => load()}
            disabled={loading || !!busy}
          >
            <FaSyncAlt /> Refresh
          </button>
          <button
            type="button"
            className="jobber-links__button jobber-links__button--primary"
            onClick={() => runAuto(true)}
            disabled={loading || !!busy}
          >
            <FaMagic /> {busy === 'preview' ? 'Checking…' : 'Check for matches'}
          </button>
        </div>
      </div>

      {error && (
        <div className="jobber-links__alert jobber-links__alert--error">
          <FaExclamationTriangle /> {error}
        </div>
      )}
      {notice && <div className="jobber-links__alert jobber-links__alert--ok">{notice}</div>}

      {!loading && (
        <div className="jobber-links__stats">
          <div className="jobber-links__stat">
            <span>{linkedCount} of {buildings.length}</span>
            <small>buildings linked</small>
          </div>
          <div className="jobber-links__stat">
            <span>{unclaimed.length}</span>
            <small>master jobs no building claims</small>
          </div>
          <div className={`jobber-links__stat${stale.length ? ' jobber-links__stat--warn' : ''}`}>
            <span>{stale.length}</span>
            <small>links Jobber no longer recognises</small>
          </div>
        </div>
      )}

      {preview && (
        <div className="jobber-links__panel jobber-links__preview">
          <div className="jobber-links__preview-head">
            <strong>
              {preview.changed.length
                ? `${preview.changed.length} link${preview.changed.length === 1 ? '' : 's'} to write`
                : 'No changes to make'}
            </strong>
            <div className="jobber-links__actions">
              {preview.changed.length > 0 && (
                <button
                  type="button"
                  className="jobber-links__button jobber-links__button--primary"
                  onClick={() => runAuto(false)}
                  disabled={!!busy}
                >
                  {busy === 'apply' ? 'Applying…' : `Apply ${preview.changed.length}`}
                </button>
              )}
              <button
                type="button"
                className="jobber-links__button"
                onClick={() => setPreview(null)}
                disabled={!!busy}
              >
                Dismiss
              </button>
            </div>
          </div>
          {preview.changed.map(c => (
            <div key={c.buildingId} className="jobber-links__preview-row">
              <strong>{c.name}</strong>
              <span className="jobber-links__arrow">→</span>
              <span>{c.jobTitle} {c.jobNumber ? `#${c.jobNumber}` : ''}</span>
              <em>score {c.score}</em>
            </div>
          ))}
          {preview.unmatched.length > 0 && (
            <p className="jobber-links__preview-note">
              No confident match for {preview.unmatched.map(u => u.name).join(', ')}. Either the
              master job doesn&apos;t exist in Jobber yet, or its title doesn&apos;t carry the
              building&apos;s street number — pick it by hand below.
            </p>
          )}
        </div>
      )}

      <div className="jobber-links__toolbar">
        <label className="jobber-links__search">
          <FaSearch />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search buildings, clients or jobs"
            aria-label="Search buildings"
          />
        </label>
      </div>

      <div className="jobber-links__panel">
        {loading ? (
          <p className="jobber-links__empty">Loading from Jobber…</p>
        ) : rows.length === 0 ? (
          <p className="jobber-links__empty">No buildings match that search.</p>
        ) : (
          <table className="jobber-links__table">
            <thead>
              <tr>
                <th>Building</th>
                <th>Jobber client</th>
                <th>Master job</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map(b => {
                const current = b.jobberMechJobId || '';
                const chosen = pending[b.id] !== undefined ? pending[b.id] : current;
                const dirty = chosen !== current;
                const isStale = current && !knownJobIds.has(current);
                const chosenJob = jobsById[chosen];
                // Other buildings on the same job. Worth naming so a shared
                // master job is visible, but it is not a conflict: saving this
                // row leaves their links untouched.
                const alsoOn = (claimants[chosen] || []).filter(name => name !== b.name);
                return (
                  <tr key={b.id}>
                    <td>
                      <strong>{b.name}</strong>
                      {b.address && <span className="jobber-links__detail">{b.address}</span>}
                    </td>
                    <td>
                      {b.jobberClientName
                        ? <span className="jobber-links__detail-strong">{b.jobberClientName}</span>
                        : <span className="jobber-links__muted">No client linked</span>}
                    </td>
                    <td>
                      <select
                        className="jobber-links__select"
                        value={chosen}
                        disabled={savingId === b.id}
                        onChange={e => setPending(p => ({ ...p, [b.id]: e.target.value }))}
                        aria-label={`Master job for ${b.name}`}
                      >
                        <option value="">— Not linked —</option>
                        {/* The stored job can be absent from Jobber's search
                            results; keep it selectable so opening the picker
                            doesn't silently rewrite the row to "not linked". */}
                        {isStale && (
                          <option value={current}>
                            {b.jobberMechJobTitle || 'Current job'} (not found in Jobber)
                          </option>
                        )}
                        {masterJobs.map(j => (
                          <option key={j.id} value={j.id}>
                            {jobLabel(j)}
                            {(claimants[j.id] || []).filter(n => n !== b.name).length > 0
                              ? `  — also on ${(claimants[j.id] || []).filter(n => n !== b.name).join(', ')}`
                              : ''}
                            {DORMANT_STATUSES.has(String(j.jobStatus || '').toLowerCase()) ? `  (${j.jobStatus})` : ''}
                          </option>
                        ))}
                      </select>
                      {isStale && !dirty && (
                        <span className="jobber-links__warn">
                          <FaExclamationTriangle /> Jobber no longer returns this job
                        </span>
                      )}
                      {alsoOn.length > 0 && (
                        <span className="jobber-links__detail">
                          Shared with {alsoOn.join(', ')}
                        </span>
                      )}
                      {!dirty && b.jobberMechJobUri && (
                        <a
                          className="jobber-links__open"
                          href={b.jobberMechJobUri}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Open in Jobber <FaExternalLinkAlt />
                        </a>
                      )}
                      {dirty && chosenJob && DORMANT_STATUSES.has(String(chosenJob.jobStatus || '').toLowerCase()) && (
                        <span className="jobber-links__warn">
                          <FaExclamationTriangle /> That job is {chosenJob.jobStatus}
                        </span>
                      )}
                    </td>
                    <td className="jobber-links__row-action">
                      {dirty ? (
                        <>
                          <button
                            type="button"
                            className="jobber-links__button jobber-links__button--primary"
                            onClick={() => saveLink(b, chosen)}
                            disabled={savingId === b.id}
                          >
                            {chosen ? <FaLink /> : <FaUnlink />}
                            {savingId === b.id ? 'Saving…' : chosen ? 'Link' : 'Unlink'}
                          </button>
                          <button
                            type="button"
                            className="jobber-links__button"
                            onClick={() => setPending(p => {
                              const next = { ...p };
                              delete next[b.id];
                              return next;
                            })}
                            disabled={savingId === b.id}
                          >
                            Cancel
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && unclaimed.length > 0 && (
        <div className="jobber-links__panel jobber-links__orphans">
          <h2>Master jobs no building claims</h2>
          <p>
            These exist in Jobber with the master-job title but nothing points at them.
            Usually it means the building is named differently on our side.
          </p>
          <ul>
            {unclaimed.map(j => (
              <li key={j.id}>
                <a href={j.webUri} target="_blank" rel="noreferrer">
                  {jobLabel(j)} <FaExternalLinkAlt />
                </a>
                <span className="jobber-links__muted">
                  {j.clientName || 'No client'}
                  {j.jobStatus ? ` · ${j.jobStatus}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
